// La puerta de las pantallas que venden un plan de EMPRESA (`/subscription` → Paywall,
// `/checkout`) — ADR-2026-10-29-paywall-con-planes-por-marca. Unos 30 botones de la app
// navegan directo a `/subscription`; en vez de tocar cada uno, la pantalla decide acá:
//  - sin facturación por marca: `children` (la pantalla de siempre, sin cambios);
//  - con facturación por marca: el plan de la marca activa (`ElegirPlanDeMarca`, con el mismo
//    pedido que el "sin créditos"; un miembro ve su "Pedile al admin"). Al cerrarlo, se vuelve;
//  - sin saber la bandera: nada que se pueda comprar. Spinner mientras se lee (con tope) y,
//    si no llega, "Reintentar" o "Volver".
// Qué se muestra lo decide `vistaDelPlanDeEmpresa` (puro, testeado).

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { asegurarSaldoLeido, ultimoSaldoVigente, type Saldo } from '../../lib/saldo';
import { useUltimoSaldo } from '../../hooks/useSaldo';
import ElegirPlanDeMarca from './ElegirPlanDeMarca';
import { vistaDelPlanDeEmpresa } from './planDeMarca';

/** Cuánto se espera la lectura del saldo antes de mostrar "Reintentar". */
export const TOPE_LECTURA_PUERTA_MS = 8000;

export function PuertaDelPlanDeEmpresa({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Se suscribe al último saldo leído: si la lectura llega tarde (pasado el tope) o la hace el
  // shell, la pantalla se vuelve a decidir sola.
  const ultimo = useUltimoSaldo();
  const vigenteAhora = ultimoSaldoVigente();
  const [leido, setLeido] = useState<{ saldo: Saldo | null } | null>(null);
  const [abierto, setAbierto] = useState(true);
  // Una vez en la pantalla de siempre, se queda: un cambio del saldo guardado (un cambio de
  // marca, el cierre de sesión) no la desmonta en medio de un pago.
  const yaEsLaDeSiempre = useRef(false);

  const leyendo = !vigenteAhora && !leido;
  useEffect(() => {
    if (!leyendo) return;
    let vivo = true;
    void asegurarSaldoLeido({ topeMs: TOPE_LECTURA_PUERTA_MS }).then((saldo) => {
      if (vivo) setLeido({ saldo });
    });
    return () => {
      vivo = false;
    };
  }, [leyendo]);

  const vista = yaEsLaDeSiempre.current
    ? ({ tipo: 'catalogo' } as const)
    : vistaDelPlanDeEmpresa({
        vigente: vigenteAhora ?? leido?.saldo ?? null,
        bandera: ultimo?.facturaPorMarca ?? null,
        leyendo,
      });
  if (vista.tipo === 'catalogo') yaEsLaDeSiempre.current = true;

  // Mismo guard que el "Volver" de Facturación: sin historial in-app, back saldría de Atlas.
  const volver = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/', { replace: true });
  };

  if (vista.tipo === 'catalogo') return <>{children}</>;

  if (vista.tipo === 'plan_de_marca') {
    return (
      <ElegirPlanDeMarca
        abierto={abierto}
        brandId={vista.pedido.brandId}
        modo={vista.pedido.modo}
        onListo={() => {
          // Sale con su transición (150 ms) y recién ahí se vuelve.
          setAbierto(false);
          window.setTimeout(volver, 160);
        }}
      />
    );
  }

  if (vista.tipo === 'leyendo') {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 md:p-8 w-full max-w-xl mx-auto animate-fade-in">
      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>No pudimos cargar los datos de tu plan. Probá de nuevo en unos segundos.</span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setLeido(null)}
          className="py-2.5 px-5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium transition-colors active:scale-[0.96]"
        >
          Reintentar
        </button>
        <button
          type="button"
          onClick={volver}
          className="py-2.5 px-5 rounded-md border border-neutral-200 bg-surface-card hover:bg-neutral-50 text-brand-black/90 text-[15px] font-medium transition-colors active:scale-[0.96]"
        >
          Volver
        </button>
      </div>
    </div>
  );
}
